import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";

export interface SurveyTargetListState {
    state: LoadStateInfo;
    request?: RetrieveSurveyTargetListRequest;
    list?: SurveyQuestionTargetInfo[];
}

const initialState: SurveyTargetListState = {
    state: notStarted(),
};

export interface RetrieveSurveyTargetListRequest {
    surveyId: string;
}

export const retrieveSurveyTargetList = createAsyncThunk(
    "retrieveSurveyTargetList",
    async (param: RetrieveSurveyTargetListRequest, thunkAPI) => {
        console.debug(`retrieve survey targets for ${param.surveyId}`);

        const response = await fetch(`${getBackendUrl()}/api/survey/${param.surveyId}/targets`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveSurveyTargetListRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.surveyQuestionTargetList;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveSurveyQuestionTargetList skipped: ${status.state.state}`);
            return false;
        }
    }
);

export const surveyQuestionTargetListSlice = createSlice({
    name: "surveyQuestionTargetList",
    initialState,
    reducers: {
        check: (state, action: PayloadAction<RetrieveSurveyTargetListRequest>) => {
            if (state.state.state != LoadState.NotStarted
                && (state.request && state.request.surveyId === action.payload.surveyId)
            ) {
                console.trace(`survey target list already retrieved for survey ${action.payload.surveyId}`);
            } else {
                console.debug(`survey target list not retrieved for survey ${action.payload.surveyId}`);
                state.state = notStarted();
                state.request = action.payload;
                state.list = undefined;
            }
        },
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveSurveyTargetList.pending, (state, action) => {
                state.state = loading();
                state.list = undefined;
                state.request = action.meta.arg;
            })
            .addCase(retrieveSurveyTargetList.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
            })
            .addCase(retrieveSurveyTargetList.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

export interface SurveyQuestionTargetInfo {
    id: number;
    surveyId: number;
    target: string;
    targetId: string;
}

export const { check, clean } = surveyQuestionTargetListSlice.actions;

export const selectSurveyQuestionTargetList = (state: RootState) => state.surveyQuestionTargetList;

export default surveyQuestionTargetListSlice.reducer;
